import { useMemo } from 'react';
import { IPosition2d } from '../../../../core/types/ui/position';
import { Size } from '../../../../core/types/ui/size';
import { IconSetsValidation } from '../../../types';
import { Group } from 'react-konva';

import { SVGIconDetail } from '../../../constants/icons/icons';
import { getTextContrastColor } from '../../../helpers/cellView/cellViewHelpers';
import { UseKonvaCursor } from '../../../../core/hooks/useKonvaCursor';
import CellIcon from './CellIcon';
import { UseGridOperations } from '../../../hooks/useGridOperations';
import { getIconSetFromCategory } from '../../../helpers/cellView/iconHelpers';

interface CellIconSetProps
  extends UseKonvaCursor,
    Size,
    IPosition2d,
    Pick<UseGridOperations, 'performSheetOperation'> {
  ci: number;
  ri: number;
  rule: IconSetsValidation;
  size: number;
  sheetId: string;
  backgroundColor?: string | null;
  color?: string | null;
  isDarkTheme: boolean;
  zoom: number;
  cellValue: string;
  // renderTextGroup: () => JSX.Element
}

const CellIconSet = ({
  x,
  y,
  width,
  height,
  size,
  rule,
  sheetId,
  backgroundColor,
  color,
  isDarkTheme,
  zoom,
  cellValue,
  ci,
  ri,
  performSheetOperation,
  onMouseEnter,
  onMouseLeave,
}: CellIconSetProps) => {
  const { primaryColor, secondaryColor } = getTextContrastColor(
    backgroundColor,
    color,
    isDarkTheme
  );

  const numericCellValue = Number(cellValue);
  const { setType } = rule;

  // const { onMouseEnter, onMouseLeave } = useKonvaCursor();

  // size = 25;

  size = size * 1.65; // Adjusting size for SVG's

  const thumbnailSize: number = size;
  const thumbnailPadding = 2 * zoom;
  const cellIncludesValue = !!cellValue.length;

  const iconSets = getIconSetFromCategory(rule.category);

  const renderThumbnails = (icons: SVGIconDetail[], rowY: number) => {
    const widthAcquired = icons.length * (thumbnailSize + thumbnailPadding);
    const centerX = Math.max((width - widthAcquired) / 2, 0);

    let rowX = centerX + thumbnailPadding;
    return icons.map(({ shapes, viewBoxSize, value }) => {
      const thumbnail = (
        <CellIcon
          ci={ci}
          ri={ri}
          viewBoxSize={viewBoxSize}
          shapes={shapes}
          key={value}
          sheetId={sheetId}
          x={rowX}
          y={rowY}
          size={thumbnailSize}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          value={value}
          cellIncludesValue={cellIncludesValue}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          performSheetOperation={performSheetOperation}
          setType={setType}
          numericCellValue={numericCellValue}
          cellValue={cellValue}
        />
      );
      rowX += thumbnailSize + thumbnailPadding;
      return thumbnail;
    });
  };

  const thumbnailRows = useMemo(() => {
    if (!iconSets) return;
    const thumbnailsPerRow = Math.max(
      Math.floor(width / (thumbnailSize + thumbnailPadding * 2)),
      1
    );
    const rows: SVGIconDetail[][] = [];
    let iter = 0;
    const max = iconSets?.length ?? 0;
    for (let i = 0; i < max; i += thumbnailsPerRow) {
      const y = iter * thumbnailSize + (iter + 1) * thumbnailPadding;
      if (y <= height) rows.push(iconSets.slice(i, i + thumbnailsPerRow));
      iter++;
    }
    return rows;
  }, [width, height, iconSets]);

  const heightAcquired =
    (thumbnailRows?.length || 0) * (thumbnailSize + thumbnailPadding);
  const centerY = Math.max((height - heightAcquired) / 2, 0);

  return (
    <Group x={x} y={y}>
      {thumbnailRows?.map((row, index) => {
        const rowY =
          centerY + index * thumbnailSize + (index + 1) * thumbnailPadding;

        return <Group key={index}>{renderThumbnails(row, rowY)}</Group>;
      })}
    </Group>
  );
};

export default CellIconSet;
